import { CheckCircle2, GripVertical, MoreVertical, PencilLine, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { WidgetWorkspaceWidgetWorkspaceReadModel } from '@r/api/generated/client.schemas.ts';
import { Card, CardContent } from '@r/components/ui/card.tsx';
import { Badge } from '@r/components/ui/badge.tsx';
import { Button } from '@r/components/ui/button.tsx';
import { Switch } from '@r/components/ui/switch.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@r/components/ui/dropdown-menu.tsx';
import {
  SortableDragHandle,
  SortableList,
  type SortableListRenderItemProps
} from '@r/components/ui/sortable-list.tsx';
import {
  getWidgetSlideCount,
  type ContentBlockPageId
} from '@r/helpers/widget-workspace-slides.ts';
import { CONTENT_BLOCK_PAGES } from '@admin/pages/personalization/content-block-pages.ts';
import {
  formatHubActiveSlideCount,
  formatHubBlockScheduleLabel
} from '@admin/pages/personalization/content-block-hub-labels.ts';
import { cn } from '@r/utils/utils.ts';

type WidgetWorkspace = WidgetWorkspaceWidgetWorkspaceReadModel;

const getBlockLabel = (block: WidgetWorkspace) => block.name || block.widget.label;

interface ContentBlockHubBoardProps {
  selectedPageId: ContentBlockPageId;
  onSelectPage: (pageId: ContentBlockPageId) => void;
  blockCountByPage: Record<ContentBlockPageId, number>;
  orderedBlocks: WidgetWorkspace[];
  canAddBlock: boolean;
  isSavingOrder: boolean;
  togglingBlockId: number | null;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onSelectBlock: (workspaceId: number) => void;
  onAddBlock: () => void;
  onDeleteBlock: (workspaceId: number, label: string) => void;
  onToggleActive: (workspaceId: number, isActive: boolean) => void;
}

/** Hub blocs de contenu : pages à gauche, liste réordonnable à droite. */
export const ContentBlockHubBoard = ({
  selectedPageId,
  onSelectPage,
  blockCountByPage,
  orderedBlocks,
  canAddBlock,
  isSavingOrder,
  togglingBlockId,
  onReorder,
  onSelectBlock,
  onAddBlock,
  onDeleteBlock,
  onToggleActive
}: ContentBlockHubBoardProps) => {
  const { t } = useTranslation();
  const selectedPage =
    CONTENT_BLOCK_PAGES.find((page) => page.id === selectedPageId) ?? CONTENT_BLOCK_PAGES[0];
  const selectedPageLabel = t(selectedPage.labelKey);

  const getBlockSubtitle = (block: WidgetWorkspace) => {
    if (!block.isActive) {
      return t('admin.contentblock.hub.block.inactive.subtitle');
    }
    return t('admin.contentblock.hub.block.subtitle', {
      widget: block.widget.label,
      page: selectedPageLabel
    });
  };

  const getActiveSlideCountLabel = (block: WidgetWorkspace) => {
    const count = getWidgetSlideCount(block.widgetItemConfigurations ?? []);
    return formatHubActiveSlideCount(count, t);
  };

  const renderBlockRow = (
    block: WidgetWorkspace,
    { index, isDragging, isOverlay, dragHandleProps }: SortableListRenderItemProps
  ) => {
    const blockId = block.id ?? 0;
    const scheduleLabel = formatHubBlockScheduleLabel(block, t);

    return (
      <div
        data-block-card
        className={cn(
          'flex items-center gap-2 rounded-xl border px-3 py-3 transition-colors duration-200',
          'border-border/60 bg-muted/20 shadow-sm',
          !isOverlay && !isDragging && 'hover:border-border hover:bg-muted/35 hover:shadow-md',
          isOverlay && 'border-primary/30 bg-card shadow-none',
          isDragging && !isOverlay && 'border-dashed border-primary/40',
          !block.isActive && !isDragging && 'opacity-65'
        )}
      >
        <SortableDragHandle
          dragHandleProps={dragHandleProps}
          aria-label={t('admin.contentblock.hub.drag.handle')}
          className={cn(
            'flex justify-center items-center w-9 h-9 rounded-lg shrink-0 touch-none',
            'border text-muted-foreground bg-background/80 border-border/60',
            isSavingOrder
              ? 'opacity-40 cursor-not-allowed'
              : 'cursor-grab hover:text-foreground hover:border-primary/30 hover:bg-primary/5 active:cursor-grabbing'
          )}
        >
          <GripVertical className="w-4 h-4" aria-hidden />
        </SortableDragHandle>

        <span
          role="img"
          className={cn(
            'flex justify-center items-center w-8 h-8 text-xs font-bold tabular-nums rounded-full shrink-0',
            'ring-1 bg-primary/12 text-primary ring-primary/20'
          )}
          aria-label={t('admin.contentblock.hub.block.order', {
            order: index + 1
          })}
        >
          {index + 1}
        </span>

        <button
          type="button"
          className="flex-1 min-w-0 text-left"
          onClick={() => onSelectBlock(blockId)}
        >
          <p className="text-sm font-semibold truncate">{getBlockLabel(block)}</p>
          <p className="text-xs truncate text-muted-foreground">{getBlockSubtitle(block)}</p>
          <p className="text-xs truncate text-muted-foreground">
            {getActiveSlideCountLabel(block)}
          </p>
          {scheduleLabel ? (
            <p className="text-xs truncate text-muted-foreground">{scheduleLabel}</p>
          ) : null}
        </button>

        <div className="flex gap-2 items-center shrink-0">
          <Switch
            checked={block.isActive}
            switchSize="sm"
            disabled={togglingBlockId === blockId || isOverlay}
            onCheckedChange={(checked) => onToggleActive(blockId, checked)}
            aria-label={t('admin.contentblock.hub.toggle.active', {
              label: getBlockLabel(block)
            })}
          />
          <Badge
            variant="outline"
            className={cn(
              'border-transparent text-xs font-normal',
              block.isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
            )}
          >
            {block.isActive
              ? t('admin.contentblock.workspace.active')
              : t('admin.contentblock.workspace.inactive')}
          </Badge>
        </div>

        {!isOverlay ? (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  rounded="full"
                  className="w-8 h-8 shrink-0 text-muted-foreground"
                  aria-label={t('admin.contentblock.hub.block.actions')}
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onSelectBlock(blockId)}>
                  <PencilLine className="mr-2 w-4 h-4" />
                  {t('admin.contentblock.hub.edit.block')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              rounded="full"
              className="w-8 h-8 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={() => onDeleteBlock(blockId, getBlockLabel(block))}
              aria-label={t('admin.contentblock.sidebar.delete.block', {
                label: getBlockLabel(block)
              })}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        ) : null}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4 w-full lg:flex-row lg:items-start">
      <Card className="w-full rounded-3xl shrink-0 lg:w-72">
        <CardContent className="p-4">
          <p className="mb-3 text-xs font-medium tracking-wide uppercase text-muted-foreground">
            {t('admin.contentblock.sidebar.pages')}
          </p>
          <div className="space-y-2">
            {CONTENT_BLOCK_PAGES.map(({ id, labelKey, Icon }) => {
              const isSelected = selectedPageId === id;

              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => onSelectPage(id)}
                  className={cn(
                    'flex gap-3 items-center p-3 w-full text-left rounded-xl border-2 transition-colors',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-transparent bg-muted/40 hover:bg-muted/60'
                  )}
                >
                  <Icon className="w-5 h-5 shrink-0 text-muted-foreground" />
                  <span className="flex-1 text-sm font-medium">{t(labelKey)}</span>
                  <span className="text-sm tabular-nums text-muted-foreground">
                    {blockCountByPage[id]}
                  </span>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="flex-1 min-w-0 rounded-3xl">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 justify-between items-start mb-2">
            <div className="space-y-1">
              <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground">
                {t('admin.contentblock.sidebar.blocks', { page: selectedPageLabel.toUpperCase() })}
              </p>
              {orderedBlocks.length > 1 && !isSavingOrder ? (
                <p className="text-xs text-muted-foreground">
                  {t('admin.contentblock.hub.order.hint')}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              {isSavingOrder && (
                <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <CheckCircle2 className="w-4 h-4 animate-pulse" aria-hidden />
                  {t('admin.contentblock.hub.order.saving')}
                </span>
              )}
              <Button
                type="button"
                rounded="full"
                disabled={!canAddBlock || isSavingOrder}
                onClick={onAddBlock}
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                {t('admin.contentblock.hub.add.block')}
              </Button>
            </div>
          </div>

          {orderedBlocks.length === 0 ? (
            <p className="px-1 py-6 text-sm text-center text-muted-foreground">
              {t('admin.contentblock.sidebar.no.blocks')}
            </p>
          ) : (
            <SortableList
              items={orderedBlocks}
              getItemId={(block) => block.id ?? 0}
              disabled={isSavingOrder}
              onReorder={onReorder}
              className="space-y-2"
              renderItem={renderBlockRow}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
};
